import { NotetakerPolicySettingKey } from "@/schema"

export interface SettingCopy {
  title: string
  description: string
  section: string
  on?: string
  off?: string
}

export const SETTING_COPY: Record<NotetakerPolicySettingKey, SettingCopy> = {
  automatic_attendance: {
    section: "Attendance",
    title: "Automatic attendance",
    description: "Send the notetaker to eligible meetings without a manual invite.",
    on: "Joins automatically",
    off: "Manual invites only",
  },
  skip_recurring_meetings: {
    section: "Attendance",
    title: "Recurring meetings",
    description: "Whether recurring meetings are skipped when attending automatically.",
    on: "Skipped",
    off: "Attended",
  },
  skip_internal_meetings: {
    section: "Attendance",
    title: "Skip internal meetings",
    description: "Skip meetings where all participants are internal to your company.",
    on: "Skipped",
    off: "Attended",
  },
  capture_mode: {
    section: "Capture",
    title: "Capture mode",
    description:
      "What the notetaker keeps from a meeting. Transcript only records no audio or video and turns off in-app recording for every book under this level.",
  },
  announcement_required: {
    section: "Capture",
    title: "Recording announcement",
    description: "Play an announcement when the notetaker records a meeting.",
    on: "Required",
    off: "Not required",
  },
  announcement_text: {
    section: "Capture",
    title: "Announcement message",
    description: "Read aloud by the bot when an announcement is required. Leave blank to use the standard message.",
  },
  consent_tracking: {
    section: "Consent",
    title: "Client consent",
    description:
      "Track whether the client consented to the notetaker. Detection reads the transcript; confirmation asks the advisor to attest before AI notes are shown.",
  },
  transcript_email: {
    section: "Transcripts and retention",
    title: "Email transcript to advisor",
    description: "Email the transcript to the book principal after each recording.",
    on: "Enabled",
    off: "Disabled",
  },
  transcript_visibility: {
    section: "Transcripts and retention",
    title: "Transcript visibility",
    description:
      "Whether advisors can open, download, export, or ask Book Chat about transcripts. Applies to meetings captured after a change; existing transcripts are unchanged. AI summaries are always available.",
  },
  retention: {
    section: "Transcripts and retention",
    title: "Recording retention",
    description:
      "How long notetaker recordings and transcripts are kept. Applies to recordings captured after a change; existing recordings keep their retention.",
  },
  internal_meetings_private: {
    section: "Privacy",
    title: "Internal meeting access",
    description:
      "When restricted, only the book principal, invited users, and assigned meeting users with book access can view internal meetings. Otherwise, anyone with book access can view them unless the meeting is marked private.",
    on: "Restricted",
    off: "Follow book permissions",
  },
  ai_notice: {
    section: "AI content",
    title: "AI notice",
    description: "Show a notation under AI summaries and meeting prep that they were created using AI.",
    on: "Shown",
    off: "Hidden",
  },
  ai_notice_text: {
    section: "AI content",
    title: "Notice wording",
    description:
      "Shown under every AI summary and meeting prep while the notice is on. Leave blank for the standard wording.",
  },
  outside_recordings: {
    section: "Recording sources",
    title: "Outside recordings",
    description:
      "Let advisors upload recordings or transcripts made outside Slant, and import them from other notetakers.",
    on: "Allowed",
    off: "Blocked",
  },
  phone_recording: {
    section: "Recording sources",
    title: "Phone call recording",
    description:
      "Record calls through Slant's phone number and import call recordings and call transcripts from phone integrations. SMS and call logs are unaffected.",
    on: "Allowed",
    off: "Blocked",
  },
}

export const SETTING_ORDER: NotetakerPolicySettingKey[] = [
  "automatic_attendance",
  "skip_recurring_meetings",
  "skip_internal_meetings",
  "capture_mode",
  "announcement_required",
  "announcement_text",
  "consent_tracking",
  "transcript_email",
  "transcript_visibility",
  "retention",
  "internal_meetings_private",
  "ai_notice",
  "ai_notice_text",
  "outside_recordings",
  "phone_recording",
]

export const SETTING_LABELS = Object.fromEntries(
  Object.entries(SETTING_COPY).map(([key, copy]) => [key, copy.title])
) as Record<NotetakerPolicySettingKey, string>
